import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { Modal } from '../components/Modal.js';
import { Alert, Button, Field, TextInput } from '../components/ui.js';

/**
 * Permanent removal is an administrator-only action; the number must be retyped and a
 * reason given so a slip cannot erase the record.
 */
export function PurgeWorkOrderButton({ workOrder }: { workOrder: WorkOrderDetail }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const purge = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}`, {
        method: 'DELETE',
        body: { reason: reason.trim() },
      }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/work-orders', { replace: true });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The work order could not be deleted. Please try again.',
      );
    },
  });

  if (!can(PERMISSIONS.WORK_ORDER_PURGE)) return null;

  const matches =
    confirmation.trim().toUpperCase() === workOrder.workOrderNumber.toUpperCase() &&
    reason.trim().length >= 5;

  return (
    <>
      <Button
        variant="danger"
        onClick={() => {
          setError(null);
          setConfirmation('');
          setReason('');
          setOpen(true);
        }}
      >
        Delete permanently
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Permanently delete ${workOrder.workOrderNumber}?`}
        description="This removes the work order, its proposals, documents, comments and history from the system, whatever its status. It cannot be undone and USPS will no longer see it. The deletion and your reason are kept in the audit log."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={!matches}
              loading={purge.isPending}
              onClick={() => {
                setError(null);
                purge.mutate();
              }}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {error && <Alert variant="error">{error}</Alert>}
          <Field label="Reason for deleting" htmlFor="purge-reason" required>
            <TextInput
              id="purge-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. duplicate of RE-TUL-000012; entered by mistake"
            />
          </Field>
          <Field
            label={`Type ${workOrder.workOrderNumber} to confirm`}
            htmlFor="purge-confirmation"
            required
          >
            <TextInput
              id="purge-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoFocus
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
